import ms from 'ms';

export const SHIPPING_QUOTE_CACHE_TIME = ms('30m');

export type QuoteLot = {
    crate: string;
    dimensions: {
        depth: number;
        height: number;
        width: number;
    };
    fragility: string;
    itemId: number;
    lotName: string;
    quantity: number;
    shipFrom:
        | string
        | {
              city: string;
              country: string;
              region: string;
          };
    weight: string;
};

export type QuoteBreakdown = {
    insuranceCost: number;
    otherCost: number;
    packagingCost: number;
    payoutToHouse: number;
    pickupCost: number;
    transportationCost: number;
};

export type BaseShippingQuote = {
    actualHandlingAndInsurance: number;
    actualTotal: number;
    breakdown: QuoteBreakdown;
    handlingAndInsurance: number;
    isFlatRate: boolean;
    quoteId: string;
    shippingCarrier: string;
    shippingProgram: string;
    total: number;
};

export type ItemShippingQuote = BaseShippingQuote & {
    city: string;
    country: string;
    lots: QuoteLot[];
    postalCode: string;
    shippingProvider: number;
    state: string;
};

export type InvoiceShippingQuote = BaseShippingQuote & {
    auctionDetails: {
        catalogId: number;
        house: string;
        houseId: number;
        name: string;
    };
    lots: QuoteLot[];
    shipToLocation: string;
    shippingProvider: string;
};

export type ShippingQuote = ItemShippingQuote | InvoiceShippingQuote;

export type ShippingQuoteSlice = {
    byId: {
        [id: number]: ShippingQuote;
    };
    error: string;
    loaded: {
        [id: number]: number;
    };
    loading: number[];
    result: 'success' | 'failure';
    type: 'invoice' | 'lot';
};

export const emptyItemShippingQuote: ItemShippingQuote = {
    actualHandlingAndInsurance: 0,
    actualTotal: 0,
    breakdown: {
        insuranceCost: 0,
        otherCost: 0,
        packagingCost: 0,
        payoutToHouse: 0,
        pickupCost: 0,
        transportationCost: 0,
    },
    city: '',
    country: '',
    handlingAndInsurance: 0,
    isFlatRate: false,
    lots: [
        {
            crate: '',
            dimensions: { depth: 0, height: 0, width: 0 },
            fragility: '',
            itemId: 0,
            lotName: '',
            quantity: 0,
            shipFrom: { city: '', country: '', region: '' },
            weight: '',
        },
    ],
    postalCode: '',
    quoteId: '',
    shippingCarrier: '',
    shippingProgram: '',
    shippingProvider: undefined,
    state: '',
    total: 0,
};

export const emptyInvoiceShippingQuote: InvoiceShippingQuote = {
    actualHandlingAndInsurance: 0,
    actualTotal: 0,
    auctionDetails: {
        catalogId: 0,
        house: '',
        houseId: 0,
        name: '',
    },
    breakdown: {
        insuranceCost: 0,
        otherCost: 0,
        packagingCost: 0,
        payoutToHouse: 0,
        pickupCost: 0,
        transportationCost: 0,
    },
    handlingAndInsurance: 0,
    isFlatRate: false,
    lots: [],
    quoteId: '',
    shippingCarrier: '',
    shippingProgram: '',
    shippingProvider: undefined,
    shipToLocation: '',
    total: 0,
};

export const defaultShippingQuoteSlice: ShippingQuoteSlice = {
    byId: {},
    error: undefined,
    loaded: {},
    loading: [],
    result: undefined,
    type: undefined,
};
